import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
    GET_UPI_ATM_TRANSACTION_HISTORY,
    UPI_ATM_CHECK_STATUS,
    FING_UPI_CW_CHECK_STATUS
} from './../../Utils/appConstants';
import { getRequest, postRequest } from './../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Space, Tag } from 'antd';
import { PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { getUTCToLocalDateTimeFormat, toDayDate } from '../../Utils/function';
import { toast } from 'react-toastify';
import PropagateLoader from "react-spinners/PropagateLoader";

// ✅ VPA Masking helper function
const maskVpa = (vpa) => {
    if (!vpa || !vpa.includes('@')) return vpa || '-';
    const atIndex = vpa.indexOf('@');
    const name = vpa.substring(0, atIndex);
    const handle = vpa.substring(atIndex);

    let maskedName;
    if (name.length <= 2) {
        maskedName = name + '***';
    } else if (name.length <= 4) {
        maskedName = name.substring(0, 2) + '***';
    } else {
        maskedName = name.substring(0, 2) + '***' + name.substring(name.length - 1);
    }

    return maskedName + handle;
};

// ============================================================
// PROVIDER HELPERS
// ============================================================
// FingPay rows carry partnerRequestId (starts with "SQ") and possibly an
// ipayId like "FING-<partnerRequestId>". InstantPay rows carry only ipayId.

const isFingPayRow = (row) => {
    if (!row) return false;
    if (row.provider === 'FINGPAY') return true;
    const pr = row.partnerRequestId || row.externalRef || '';
    if (String(pr).startsWith('SQ')) return true;
    const ip = row.ipayId || '';
    if (String(ip).startsWith('FING-')) return true;
    return false;
};

// Stable identifier for a row — used as the loading-state key, for the
// invoice route param, and for status-check payload construction.
const getRowKey = (row) => {
    if (!row) return '';
    if (isFingPayRow(row)) {
        return row.partnerRequestId || row.externalRef || row.ipayId || '';
    }
    return row.ipayId || row.externalRef || '';
};

function UpiAtmHistory(props) {

    const history = useHistory();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [searchTransId, setSearchTransId] = useState('');
    const [searchMobile, setSearchMobile] = useState('');
    const [searchStatus, setSearchStatus] = useState('');
    const [endDate, setEndDate] = useState(toDayDate());
    const [startDate, setStartDate] = useState(toDayDate());
    const [getData, setData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);
    const [checkingStatus, setCheckingStatus] = useState({});

    const buildQueryParams = (pageNum, limitNum) => {
        let params = { page: pageNum, limit: limitNum };
        if (startDate) params.startDate = typeof startDate === 'string' ? startDate : startDate.format('DD-MM-YYYY');
        if (endDate) params.endDate = typeof endDate === 'string' ? endDate : endDate.format('DD-MM-YYYY');
        const searchVal = searchTransId || searchMobile;
        if (searchVal && searchVal.trim() !== '') params.searchText = searchVal;
        if (searchStatus && searchStatus.trim() !== '') params.status = searchStatus;
        return params;
    };

    // ============================================================
    // CHECK STATUS — one function, both providers
    // ============================================================
    const handleCheckStatus = async (record) => {
        const fingpay = isFingPayRow(record);
        const rowKey = getRowKey(record);

        if (!rowKey) {
            toast.error('Transaction ID missing on this row');
            return;
        }

        setCheckingStatus(prev => ({ ...prev, [rowKey]: true }));

        try {
            if (fingpay) {
                // ------------------------------------------------
                // FingPay UPI CW status check
                // ------------------------------------------------
                const partnerRequestId =
                    record.partnerRequestId ||
                    record.externalRef ||
                    String(record.ipayId || '').replace(/^FING-/, '');

                const payload = { partnerRequestId, devicetype: 'W' };
                const response = await postRequest(FING_UPI_CW_CHECK_STATUS, payload, history);

                if (response.success && response.data) {
                    const d = response.data;
                    const newStatus = (d.status || d.qrStatus || '').toUpperCase();

                    setData(prevData =>
                        prevData.map(item => {
                            const sameRow =
                                item.partnerRequestId === partnerRequestId ||
                                item.externalRef === partnerRequestId ||
                                item.ipayId === record.ipayId;
                            if (!sameRow) return item;

                            return {
                                ...item,
                                status: newStatus,
                                qrStatus: newStatus,
                                qrStatusMsg: d.qrStatusMsg || d.responseMessage || 'Status updated',
                                fingpayTxnId: d.fingpayTxnId || item.fingpayTxnId,
                                bankRrn: d.bankRrn || item.bankRrn,
                                operatorId: d.bankRrn || item.operatorId,
                                payerName: d.payer?.name || item.payerName,
                                payerVpa: d.payer?.vpa || item.payerVpa,
                                payerAccountNumber: d.payer?.maskedAccount || item.payerAccountNumber,
                                openingBalance: d.openingBalance ?? item.openingBalance,
                                closingBalance: d.closingBalance ?? item.closingBalance,
                                responseCode: d.responseCode || item.responseCode,
                                responseMessage: d.responseMessage || item.responseMessage,
                            };
                        })
                    );

                    if (newStatus === 'SUCCESS') toast.success('Transaction completed!');
                    else if (newStatus === 'FAILED') toast.error('Transaction failed.');
                    else if (newStatus === 'EXPIRED') toast.warning('QR expired.');
                    else if (newStatus === 'PENDING') toast.info('Payment pending — ask the customer to pay.');
                    else toast.info(`Status: ${newStatus || 'unknown'}`);
                } else {
                    toast.error(response.message || 'Failed to check status');
                }

            } else {
                // ------------------------------------------------
                // InstantPay status check (existing behaviour)
                // ------------------------------------------------
                const payload = { ipayId: record.ipayId, devicetype: 'W' };
                const response = await postRequest(UPI_ATM_CHECK_STATUS, payload, history);

                if (response.success && response.data) {
                    const newStatus = response.data.qrStatus;

                    setData(prevData => prevData.map(item =>
                        item.ipayId === record.ipayId
                            ? {
                                ...item,
                                status: newStatus,
                                qrStatus: newStatus,
                                qrStatusMsg: response.data.qrStatusMsg || 'Status updated',
                                payerName: response.data.payer?.name || item.payerName,
                                payerVpa: response.data.payer?.vpa || item.payerVpa,
                                payerAccountNumber: response.data.payer?.maskedAccount || item.payerAccountNumber,
                            }
                            : item
                    ));

                    if (newStatus === 'SUCCESS') toast.success('Transaction completed!');
                    else if (newStatus === 'FAILED') toast.error('Transaction failed.');
                    else if (newStatus === 'EXPIRED') toast.warning('QR expired.');
                    else toast.info(`Status: ${newStatus}`);
                } else {
                    toast.error(response.message || 'Failed to check status');
                }
            }
        } catch (err) {
            toast.error(err.message || 'Failed to check status');
        } finally {
            setCheckingStatus(prev => ({ ...prev, [rowKey]: false }));
        }
    };

    // ============================================================
    // COLUMNS
    // ============================================================
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "145px",
        },
        {
            name: 'Trans ID',
            cell: row => {
                // FingPay rows: partnerRequestId (SQ…) is the meaningful ID.
                // InstantPay rows: externalRef.
                const display = isFingPayRow(row)
                    ? (row.partnerRequestId || row.externalRef || row.ipayId)
                    : (row.externalRef || row.ipayId);
                return (
                    <Tooltip placement="bottom" title={display}>
                        <span style={{ fontSize: '12px' }}>{display || '-'}</span>
                    </Tooltip>
                );
            },
            width: "170px",
        },
        // {
        //     name: 'Provider',
        //     cell: row => (
        //         <Tag
        //             color={isFingPayRow(row) ? 'blue' : 'green'}
        //             style={{ fontSize: '10px', borderRadius: '10px' }}
        //         >
        //             {isFingPayRow(row) ? 'FINGPAY' : 'INSTANTPAY'}
        //         </Tag>
        //     ),
        //     width: "110px",
        //     center: true,
        // },
        {
            name: 'Mobile',
            selector: row => row.customerMobile || '-',
            width: "110px",
        },
        {
            name: 'Customer',
            cell: row => row.payerName || row.customerName || '-',
            width: "130px",
        },
        {
            name: 'Payer VPA',
            cell: row => (
                <Tooltip placement="bottom" title={maskVpa(row.payerVpa)}>
                    <span style={{ fontSize: '11px' }}>
                        {row.payerVpa ? maskVpa(row.payerVpa) : '-'}
                    </span>
                </Tooltip>
            ),
            width: "145px",
        },
        {
            name: 'Account',
            cell: row => (
                <Tooltip placement="bottom" title={row.payerAccountNumber}>
                    <span style={{ fontSize: '11px' }}>{row.payerAccountNumber || '-'}</span>
                </Tooltip>
            ),
            width: "140px",
        },
        {
            name: 'Bank RRN',
            cell: row => {
                // FingPay stores RRN in bankRrn, InstantPay in operatorId.
                const rrn = row.bankRrn || row.operatorId || '-';
                return (
                    <Tooltip placement="bottom" title={rrn}>
                        <span style={{ fontSize: '11px' }}>{rrn}</span>
                    </Tooltip>
                );
            },
            width: "125px",
        },
        {
            name: 'Amount',
            selector: row => `₹${row.amount || 0}`,
            width: "90px",
            sortable: true,
        },
        {
            name: 'Status',
            cell: row => {
                const statusConfig = {
                    INITIATED: { color: '#1890ff', text: 'Initiated' },
                    PENDING:   { color: '#faad14', text: 'Pending' },
                    SUCCESS:   { color: '#52c41a', text: 'Success' },
                    FAILED:    { color: '#ff4d4f', text: 'Failed' },
                    EXPIRED:   { color: '#faad14', text: 'Expired' }
                };
                const config = statusConfig[row.status] || { color: '#d9d9d9', text: row.status || 'Unknown' };
                return <Tag color={config.color} style={{ borderRadius: '12px' }}>{config.text}</Tag>;
            },
            width: "105px",
        },
        {
            name: 'Action',
            cell: (record) => {
                const key = getRowKey(record);
                const isBusy = !!checkingStatus[key];
                const canCheck = record.status === 'INITIATED' || record.status === 'PENDING';

                return (
                    <Space size="small">
                        {canCheck && (
                            <Tooltip title="Check Status">
                                <ReloadOutlined
                                    className="pointer"
                                    style={{
                                        fontSize: "16px",
                                        color: "#1890ff",
                                        animation: isBusy ? 'spin 1s linear infinite' : 'none'
                                    }}
                                    onClick={() => isBusy ? null : handleCheckStatus(record)}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Print Invoice">
                            <PrinterOutlined
                                className="pointer"
                                style={{ fontSize: "16px", color: "#C9262C" }}
                                onClick={() => history.push(`/upi-atm-invoice/${key}`)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
            width: "90px",
            center: true,
        },
        {
            name: 'Message',
            cell: row => (
                <Tooltip placement="bottom" title={row.qrStatusMsg || row.failureReason}>
                    <span style={{
                        fontSize: '11px',
                        color: row.status === 'FAILED' ? '#ff4d4f'
                            : row.status === 'SUCCESS' ? '#52c41a'
                            : row.status === 'PENDING' ? '#d48806'
                            : '#666'
                    }}>
                        {row.qrStatusMsg || row.failureReason || '-'}
                    </span>
                </Tooltip>
            ),
            width: "160px",
        }
    ];

    // ============================================================
    // DATA FETCH
    // ============================================================
    const fetchTransactions = (pageNum = 1, limitNum = limit) => {
        setLoading(true);
        const params = buildQueryParams(pageNum, limitNum);
        const queryString = Object.keys(params)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');

        getRequest(`${GET_UPI_ATM_TRANSACTION_HISTORY}?${queryString}`, history)
            .then((response) => {
                if (response.success === true && response.data) {
                    const transactions = response.data.transactions || [];
                    setData(transactions);
                    setTotalRecord(response.data.pagination?.totalRecords || transactions.length);
                    setPage(response.data.pagination?.currentPage || pageNum);
                } else {
                    toast.error(response.message || 'Failed to fetch record');
                    setData([]); setTotalRecord(0); setPage(1);
                }
                setLoading(false);
            })
            .catch(function (error) {
                setData([]); setTotalRecord(0); setPage(1);
                setLoading(false);
            });
    };

    useEffect(() => { fetchTransactions(1); }, []);

    const handlePageChange = (newPage) => { setPage(newPage); fetchTransactions(newPage, isLimit); };
    const handlePerRowsChange = (newPerPage) => { setLimit(newPerPage); fetchTransactions(1, newPerPage); };
    const onChangeStartDate = (date) => setStartDate(date);
    const onChangeEndDate = (date) => setEndDate(date);
    const handleSearch = () => { setPage(1); setTimeout(() => fetchTransactions(1, isLimit), 50); };
    const handleClear = () => {
        setSearchTransId(''); setSearchMobile(''); setSearchStatus('');
        setStartDate(toDayDate()); setEndDate(toDayDate()); setPage(1);
        setTimeout(() => fetchTransactions(1, limit), 100);
    };

    if (isLoading && getData.length === 0) {
        return (
            <div style={{ padding: '20px' }}>
                <div className="content text-center" style={{ margin: "100px 0" }}>
                    <PropagateLoader color="#C9262C" loading={true} size={20} />
                    <p style={{ marginTop: 16, color: '#999', fontSize: '13px' }}>Loading transactions...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px 8px' }}>
            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .pointer { cursor: pointer; }
            `}</style>

            {/* Filter Section */}
            <div className="row" style={{ marginBottom: '16px' }}>
                <div className='col-md-12 date-search mb-0'>
                    <div className='row'>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-blue'>From</label>
                                <DatePicker
                                    className="form-control form-control-border"
                                    value={startDate} format={dateFormat}
                                    onChange={onChangeStartDate}
                                />
                            </div>
                        </div>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>To</label>
                                <DatePicker
                                    className="form-control form-control-border"
                                    value={endDate} format={dateFormat}
                                    onChange={onChangeEndDate}
                                />
                            </div>
                        </div>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                <input type="text" className="form-control form-control-border"
                                    placeholder="Trans ID"
                                    onChange={(e) => setSearchTransId(e.target.value)} value={searchTransId} />
                            </div>
                        </div>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                <input type="text" className="form-control form-control-border"
                                    placeholder="Mobile Number"
                                    onChange={(e) => setSearchMobile(e.target.value)} value={searchMobile} />
                            </div>
                        </div>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                <select className="form-control form-control-border"
                                    onChange={(e) => setSearchStatus(e.target.value)} value={searchStatus}>
                                    <option value="">All Status</option>
                                    <option value="SUCCESS">Success</option>
                                    <option value="FAILED">Failed</option>
                                    <option value="INITIATED">Initiated</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="EXPIRED">Expired</option>
                                </select>
                            </div>
                        </div>
                        <div className='col-md-1 col-lg-1'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                <button type="submit" className="btn btn-primary rounded-pill pl-3 pr-3 btn-block"
                                    onClick={handleSearch}>Search</button>
                            </div>
                        </div>
                        <div className='col-md-1 col-lg-1'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                <button type="button" className="btn btn-default rounded-pill pl-3 pr-3 btn-block"
                                    onClick={handleClear}>Reset</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <DataTable
                title=""
                columns={columns}
                data={getData}
                highlightOnHover
                pagination
                paginationServer
                paginationTotalRows={getTotalRecord}
                paginationPerPage={isLimit}
                paginationDefaultPage={page}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                progressPending={isLoading}
                progressComponent={
                    <div style={{ padding: '30px' }}>
                        <PropagateLoader color="#C9262C" loading={true} size={12} />
                    </div>
                }
                noDataComponent={
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p style={{ color: '#999', fontSize: '14px' }}>No transactions found</p>
                    </div>
                }
                responsive
                striped
            />
        </div>
    );
}

export default UpiAtmHistory;